class AdvancedVariantPicker {
  constructor() {
    this.productData = null;
    this.selectedColors = new Set();
    this.quantities = {};
    
    this.init();
  }

  init() {
    // Load product data
    this.loadProductData();
    
    // Setup event listeners
    this.setupColorSelection();
    this.setupSelectAll();
    this.setupSortDropdown();
    this.setupAddToCart();
  }

  loadProductData() {
    const dataElement = document.getElementById('product-data');
    if (dataElement) {
      try {
        this.productData = JSON.parse(dataElement.textContent);
        console.log('Product data loaded:', this.productData);
      } catch (e) {
        console.error('Error parsing product data:', e);
      }
    }
  }

  setupColorSelection() {
    const checkboxes = document.querySelectorAll('.color-checkbox');
    
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const colorOption = e.target.closest('.color-option');
        const colorValue = e.target.value;
        
        if (e.target.checked) {
          colorOption.classList.add('selected');
          this.selectedColors.add(colorValue);
        } else {
          colorOption.classList.remove('selected');
          this.selectedColors.delete(colorValue);
          // Clean up quantities for deselected color
          this.cleanupDeselectedColors();
        }
        
        this.updateQuantitySection();
      });
    });
  }

  setupSelectAll() {
    const selectAllCheckbox = document.getElementById('select-all-colors');
    const colorCheckboxes = document.querySelectorAll('.color-checkbox');
    
    if (selectAllCheckbox) {
      selectAllCheckbox.addEventListener('change', (e) => {
        colorCheckboxes.forEach(checkbox => {
          checkbox.checked = e.target.checked;
          const event = new Event('change', { bubbles: true });
          checkbox.dispatchEvent(event);
        });
      });
    }
  }

  setupSortDropdown() {
    const sortDropdown = document.getElementById('sort-options');
    if (sortDropdown) {
      sortDropdown.addEventListener('change', (e) => {
        this.sortQuantityBlocks(e.target.value);
      });
    }
  }

  setupAddToCart() {
    const addToCartBtn = document.getElementById('bulk-add-to-cart');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => {
        this.addToCart();
      });
    }

    // Update summary when quantities change
    document.addEventListener('variantQuantityChange', () => {
      this.updateCartSummary();
    });
  }

  updateCartSummary() {
    const summary = document.getElementById('cart-summary');
    const totalItemsEl = document.getElementById('total-items');
    
    const items = this.getSelectedQuantities();
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    
    if (totalItems > 0 && summary && totalItemsEl) {
      summary.style.display = 'block';
      totalItemsEl.textContent = totalItems;
    } else if (summary) {
      summary.style.display = 'none';
    }
  }

  updateQuantitySection() {
    const quantitySection = document.getElementById('quantity-section');
    const quantityBlocks = document.getElementById('color-quantity-blocks');
    
    if (this.selectedColors.size === 0) {
      quantitySection.style.display = 'none';
      quantityBlocks.innerHTML = '';
      // Update cart summary when all colors deselected
      this.updateCartSummary();
      return;
    }
    
    quantitySection.style.display = 'block';
    quantityBlocks.innerHTML = '';
    
    // Generate quantity blocks for each selected color
    this.selectedColors.forEach(color => {
      const block = this.createQuantityBlock(color);
      quantityBlocks.appendChild(block);
    });
    
    // Update cart summary after rendering blocks
    this.updateCartSummary();
  }

  createQuantityBlock(colorValue) {
    const block = document.createElement('div');
    block.className = 'color-quantity-block';
    block.dataset.color = colorValue;
    
    // Find variant with this color
    const colorVariant = this.findVariantByColor(colorValue);
    
    // Get all sizes for this color
    const sizes = this.getSizesForColor(colorValue);
    
    block.innerHTML = `
      <div class="color-quantity-header">
        <img src="${colorVariant?.featured_image?.src || 'https://via.placeholder.com/60x60?text=' + encodeURIComponent(colorValue)}" 
             alt="${colorValue}" 
             class="color-quantity-thumbnail"
             onerror="this.src='https://via.placeholder.com/60x60?text=Image'">
        <div class="color-quantity-name">${colorValue}</div>
      </div>
      <div class="scroll-indicator">← Swipe to see all sizes →</div>
      <div class="size-table-wrapper">
        <table class="size-table">
          <thead>
            <tr>
              ${sizes.map(size => `<th class="size-header">${size}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            <tr>
              ${sizes.map(size => {
                const variant = this.findVariantByColorAndSize(colorValue, size);
                const price = variant ? (variant.price / 100).toFixed(2) : '0.00';
                const comparePrice = variant?.compare_at_price ? (variant.compare_at_price / 100).toFixed(2) : null;
                
                return `
                  <td class="price-cell">
                    <span class="sale-price-label">SALE PRICE</span>
                    <span class="price-value">$${price}</span>
                  </td>
                `;
              }).join('')}
            </tr>
            <tr>
              ${sizes.map(size => {
                const variant = this.findVariantByColorAndSize(colorValue, size);
                const variantId = variant?.id || '';
                // Temporarily disable availability check - all variants shown as available
                const available = true; // variant?.available !== false;
                // Get saved quantity for this color+size combination
                const savedQty = this.getSavedQuantity(colorValue, size);
                
                return `
                  <td class="quantity-input-cell">
                    <input 
                      type="number" 
                      min="0" 
                      value="${savedQty}"
                      data-variant-id="${variantId}"
                      data-color="${colorValue}"
                      data-size="${size}"
                      ${!available ? 'disabled' : ''}
                      class="quantity-input">
                    ${available ? '<div class="stock-available">500+</div>' : '<div style="color: #d32f2f; font-size: 12px;">Out of Stock</div>'}
                  </td>
                `;
              }).join('')}
            </tr>
          </tbody>
        </table>
      </div>
    `;
    
    // Add event listeners to quantity inputs
    const inputs = block.querySelectorAll('.quantity-input');
    inputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleQuantityChange(e));
    });
    
    return block;
  }

  findVariantByColor(color) {
    if (!this.productData) return null;
    
    // Find first variant with this color that has an image
    const variantWithImage = this.productData.variants.find(v => {
      const hasColor = v.option1 === color || v.option2 === color || v.option3 === color;
      const hasImage = v.featured_image && v.featured_image.src;
      return hasColor && hasImage;
    });
    
    // If no variant with image found, return any variant with this color
    if (!variantWithImage) {
      return this.productData.variants.find(v => 
        v.option1 === color || v.option2 === color || v.option3 === color
      );
    }
    
    return variantWithImage;
  }

  getSizesForColor(color) {
    if (!this.productData) return [];
    
    // Find size option (usually option2 or option3)
    const sizeOption = this.productData.options.find(opt => 
      opt.name.toLowerCase().includes('size')
    );
    
    if (!sizeOption) {
      // Return unique variants if no size option
      return ['One Size'];
    }
    
    return sizeOption.values;
  }

  findVariantByColorAndSize(color, size) {
    if (!this.productData) return null;
    
    return this.productData.variants.find(v => {
      const hasColor = v.option1 === color || v.option2 === color || v.option3 === color;
      const hasSize = v.option1 === size || v.option2 === size || v.option3 === size;
      return hasColor && hasSize;
    });
  }

  handleQuantityChange(event) {
    const input = event.target;
    const variantId = input.dataset.variantId;
    const color = input.dataset.color;
    const size = input.dataset.size;
    const quantity = parseInt(input.value) || 0;
    
    const key = `${color}-${size}`;
    
    if (quantity > 0) {
      this.quantities[key] = {
        variantId,
        color,
        size,
        quantity
      };
    } else {
      // Remove from quantities if set to 0
      delete this.quantities[key];
    }
    
    console.log('Quantity updated:', this.quantities);
    
    // Update cart summary
    this.updateCartSummary();
    
    // Dispatch custom event for cart integration
    document.dispatchEvent(new CustomEvent('variantQuantityChange', {
      detail: {
        variantId,
        color,
        size,
        quantity,
        allQuantities: this.quantities
      }
    }));
  }

  getSavedQuantity(color, size) {
    const key = `${color}-${size}`;
    return this.quantities[key]?.quantity || 0;
  }

  cleanupDeselectedColors() {
    // Remove quantities for colors that are no longer selected
    Object.keys(this.quantities).forEach(key => {
      const color = this.quantities[key].color;
      if (!this.selectedColors.has(color)) {
        delete this.quantities[key];
      }
    });
    this.updateCartSummary();
  }

  sortQuantityBlocks(sortType) {
    const container = document.getElementById('color-quantity-blocks');
    const blocks = Array.from(container.children);
    
    if (sortType === 'popular') {
      // Sort by popularity (you can customize this logic)
      blocks.sort((a, b) => {
        const colorA = a.dataset.color;
        const colorB = b.dataset.color;
        return colorA.localeCompare(colorB);
      });
    } else if (sortType === 'price-low') {
      blocks.sort((a, b) => {
        const priceA = this.getLowestPrice(a.dataset.color);
        const priceB = this.getLowestPrice(b.dataset.color);
        return priceA - priceB;
      });
    } else if (sortType === 'price-high') {
      blocks.sort((a, b) => {
        const priceA = this.getLowestPrice(a.dataset.color);
        const priceB = this.getLowestPrice(b.dataset.color);
        return priceB - priceA;
      });
    }
    
    // Re-append sorted blocks
    blocks.forEach(block => container.appendChild(block));
  }

  getLowestPrice(color) {
    if (!this.productData) return 0;
    
    const colorVariants = this.productData.variants.filter(v =>
      v.option1 === color || v.option2 === color || v.option3 === color
    );
    
    const prices = colorVariants.map(v => v.price);
    return Math.min(...prices);
  }

  // Public method to get all selected quantities
  getSelectedQuantities() {
    const items = [];
    
    Object.values(this.quantities).forEach(item => {
      if (item.quantity > 0) {
        items.push(item);
      }
    });
    
    return items;
  }

  // Public method to add items to cart
  async addToCart() {
    const items = this.getSelectedQuantities();
    
    if (items.length === 0) {
      this.showMessage('Please select at least one item with quantity', 'error');
      return;
    }

    const addToCartBtn = document.getElementById('bulk-add-to-cart');
    const btnText = addToCartBtn.querySelector('.cart-btn-text');
    const btnLoading = addToCartBtn.querySelector('.cart-btn-loading');
    
    // Show loading state
    addToCartBtn.disabled = true;
    btnText.style.display = 'none';
    btnLoading.style.display = 'inline-flex';
    
    const formData = {
      items: items.map(item => ({
        id: item.variantId,
        quantity: item.quantity
      }))
    };
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Success!
        this.showMessage(`Successfully added ${items.length} item(s) to cart!`, 'success');
        
        // Trigger cart update event for theme
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        // Optional: Redirect to cart after 1.5 seconds
        setTimeout(() => {
          // Uncomment next line if you want auto-redirect to cart
          // window.location.href = '/cart';
        }, 1500);
        
        // Reset quantities (optional)
        // this.resetQuantities();
        
      } else {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add items to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showMessage(error.message || 'Error adding items to cart. Please try again.', 'error');
    } finally {
      // Reset button state
      addToCartBtn.disabled = false;
      btnText.style.display = 'inline';
      btnLoading.style.display = 'none';
    }
  }

  showMessage(message, type = 'success') {
    // Remove existing messages
    const existingMessages = document.querySelectorAll('.cart-success-message, .cart-error-message');
    existingMessages.forEach(msg => msg.remove());
    
    const messageDiv = document.createElement('div');
    messageDiv.className = type === 'success' ? 'cart-success-message' : 'cart-error-message';
    messageDiv.textContent = message;
    
    const addToCartSection = document.querySelector('.add-to-cart-section');
    if (addToCartSection) {
      addToCartSection.appendChild(messageDiv);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        messageDiv.remove();
      }, 5000);
    }
  }

  resetQuantities() {
    const inputs = document.querySelectorAll('.quantity-input');
    inputs.forEach(input => {
      input.value = 0;
    });
    this.quantities = {};
    this.updateCartSummary();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.advancedVariantPicker = new AdvancedVariantPicker();
});

// Export for external use
window.AdvancedVariantPicker = AdvancedVariantPicker;